import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { makeClassName } from '@siteless/util';
import avatars from '../config/avatars';
import { TierRankPill } from './TierRankPill';
import { MobileBallotItem } from './MobileBallotItem';
import {
  getIncompleteClusterHeight,
  IncompleteAvatarGrid,
} from './IncompleteAvatarGrid';
import { TitanicScene, TITANIC_HEIGHT, SCENE_OVERLAP } from './TitanicScene';

type UserItem = {
  key: string;
  score: number;
  ArtworkComponent: (typeof avatars)[keyof typeof avatars]['ArtworkComponent'];
  handle?: string;
  avatarBackgroundHex?: string | null;
  letterboxdId?: string | null;
};

type IncompleteUserItem = {
  key: string;
  ArtworkComponent: (typeof avatars)[keyof typeof avatars]['ArtworkComponent'];
  handle?: string;
  avatarBackgroundHex?: string | null;
};

export let FlatItemType = {
  header: 'header',
  user: 'user',
  incompleteCluster: 'incompleteCluster',
  titanic: 'titanic',
} as const;

export type FlatItem =
  | {
      type: typeof FlatItemType.header;
      rank: number;
      points: number;
      tierSize: number;
      incompleteTier?: boolean;
    }
  | {
      type: typeof FlatItemType.user;
      user: UserItem;
      withinGroupIndex: number;
      points: number;
    }
  | { type: typeof FlatItemType.incompleteCluster; users: IncompleteUserItem[] }
  | { type: typeof FlatItemType.titanic };

export let SPACER_HEIGHT = 60;
let LIST_PADDING_START = SPACER_HEIGHT;
export let HEADER_HEIGHT = 74;
export let ITEM_HEIGHT = 164;
export let LAST_ITEM_HEIGHT = 180;
let SWAP_ANTI_JITTER_PX = 10;

export type MobileScoredListHandle = {
  scrollToOffset: (offset: number, opts?: { behavior?: ScrollBehavior }) => void;
  scrollToIndex: (index: number, opts?: { align?: 'start' | 'center' | 'end' | 'auto'; behavior?: ScrollBehavior }) => void;
};

let MobileScoredList = memo(forwardRef<MobileScoredListHandle, {
  flatList: FlatItem[];
  currentUserId: string;
  ownerUserId?: string;
  hasScoringStarted: boolean;
  useStrictScore: boolean;
  isPending: boolean;
  isFinalApproach: boolean;
  prefersLeftHandMode?: boolean;
  onSelectBallot: (key: string) => void;
  onRangeChange: (range: {
    startIndex: number;
    endIndex: number;
    inLastPage: boolean;
  }) => void;
}>(({
  flatList,
  currentUserId,
  ownerUserId,
  hasScoringStarted,
  useStrictScore,
  isPending,
  isFinalApproach,
  prefersLeftHandMode,
  onSelectBallot,
  onRangeChange,
}, ref) => {
  let [scrollContainer, setScrollContainer] =
    useState<HTMLDivElement | null>(null);
  let scrollContainerSyntheticRef = useMemo(
    () => ({ current: scrollContainer }),
    [scrollContainer],
  );

  let [containerHeightPx, setContainerHeightPx] = useState(0);
  useEffect(() => {
    if (!scrollContainer) return;
    let observer = new ResizeObserver(([entry]) => {
      setContainerHeightPx(entry.contentRect.height);
    });
    observer.observe(scrollContainer);
    return () => observer.disconnect();
  }, [scrollContainer]);

  let stickyIndexes = useMemo(
    () =>
      flatList.reduce<number[]>((acc, item, i) => {
        if (item.type === FlatItemType.header) acc.push(i);
        return acc;
      }, []),
    [flatList],
  );

  let stickySet = useMemo(() => new Set(stickyIndexes), [stickyIndexes]);

  let lastBeforeHeaderSet = useMemo(() => {
    let set = new Set<number>();
    for (let si of stickyIndexes) {
      if (si > 0 && flatList[si - 1]?.type === FlatItemType.user) {
        set.add(si - 1);
      }
    }
    return set;
  }, [stickyIndexes, flatList]);

  let headerStartPositions = useMemo(() => {
    let map = new Map<number, number>();
    let pos = LIST_PADDING_START;
    for (let i = 0; i < flatList.length; i++) {
      if (stickySet.has(i)) map.set(i, pos);
      let item = flatList[i];
      if (item.type === FlatItemType.incompleteCluster) {
        pos += getIncompleteClusterHeight(item.users.length);
      } else if (item.type === FlatItemType.titanic) {
        pos += TITANIC_HEIGHT - SCENE_OVERLAP;
      } else if (stickySet.has(i)) {
        pos += HEADER_HEIGHT;
      } else if (lastBeforeHeaderSet.has(i)) {
        pos += LAST_ITEM_HEIGHT;
      } else {
        pos += ITEM_HEIGHT;
      }
    }
    return map;
  }, [flatList, stickySet, lastBeforeHeaderSet]);

  let virtualizer = useVirtualizer({
    count: flatList.length,
    getScrollElement: () => scrollContainer,
    estimateSize: (index) => {
      let item = flatList[index];
      if (item.type === FlatItemType.incompleteCluster) {
        return getIncompleteClusterHeight(item.users.length);
      }
      if (item.type === FlatItemType.titanic) {
        return TITANIC_HEIGHT - SCENE_OVERLAP;
      }
      return stickySet.has(index)
        ? HEADER_HEIGHT
        : lastBeforeHeaderSet.has(index)
        ? LAST_ITEM_HEIGHT
        : ITEM_HEIGHT;
    },
    overscan: 5,
    paddingStart: LIST_PADDING_START,
    scrollPaddingStart: HEADER_HEIGHT + LIST_PADDING_START,
    getItemKey: (index) => {
      let item = flatList[index];
      if (item.type === FlatItemType.header)
        return item.incompleteTier
          ? 'header-incomplete'
          : `header-${item.rank}`;
      if (item.type === FlatItemType.incompleteCluster)
        return 'incomplete-cluster';
      if (item.type === FlatItemType.titanic) return 'titanic';
      return item.user.key;
    },
  });

  useImperativeHandle(ref, () => ({
    scrollToOffset: (offset, opts) => virtualizer.scrollToOffset(offset, opts),
    scrollToIndex: (index, opts) => virtualizer.scrollToIndex(index, opts),
  }), [virtualizer]);

  let rangeStart = virtualizer.range?.startIndex ?? 0;
  let rangeEnd = virtualizer.range?.endIndex ?? 0;
  let scrollRemainingPx =
    virtualizer.getTotalSize() - (virtualizer.scrollOffset ?? 0) - containerHeightPx;
  let scrollBottomBufferPx = 20;
  let inLastPage =
    containerHeightPx > 0 && scrollRemainingPx <= scrollBottomBufferPx;
  useEffect(() => {
    onRangeChange({ startIndex: rangeStart, endIndex: rangeEnd, inLastPage });
  }, [rangeStart, rangeEnd, inLastPage, onRangeChange]);

  let headerSwapThresholds = useMemo(() => {
    let thresholds: Array<{ threshold: number; index: number }> = [];
    for (let si of stickyIndexes) {
      let start = headerStartPositions.get(si) ?? 0;
      thresholds.push({ threshold: start - SPACER_HEIGHT, index: si });
    }
    return thresholds;
  }, [stickyIndexes, headerStartPositions]);

  let [activeHeaderIndex, setActiveHeaderIndex] = useState(
    stickyIndexes[0] ?? -1,
  );

  let activeHeaderRef = useRef(activeHeaderIndex);
  let headerSwapThresholdsRef = useRef(headerSwapThresholds);
  headerSwapThresholdsRef.current = headerSwapThresholds;

  let updateActiveHeader = useCallback((scrollTop: number) => {
    let thresholds = headerSwapThresholdsRef.current;
    let newActive = thresholds[0]?.index ?? -1;
    for (let i = thresholds.length - 1; i >= 0; i--) {
      if (scrollTop + SWAP_ANTI_JITTER_PX >= thresholds[i].threshold) {
        newActive = thresholds[i].index;
        break;
      }
    }
    if (newActive !== activeHeaderRef.current) {
      activeHeaderRef.current = newActive;
      setActiveHeaderIndex(newActive);
    }
  }, []);

  useEffect(() => {
    if (!scrollContainer) return;
    updateActiveHeader(scrollContainer.scrollTop);
    let handler = () => updateActiveHeader(scrollContainer.scrollTop);
    scrollContainer.addEventListener('scroll', handler, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handler);
  }, [scrollContainer, updateActiveHeader]);

  let activeHeader =
    activeHeaderIndex >= 0 ? flatList[activeHeaderIndex] : null;

  let virtualItems = virtualizer.getVirtualItems();

  return (
    <div
      className={makeClassName(
        'relative z-0 h-full w-full transition-opacity',
        isPending && 'opacity-50',
      )}
    >
      {activeHeader && activeHeader.type === FlatItemType.header && (
        <div
          style={{
            position: 'absolute',
            top: SPACER_HEIGHT,
            left: 0,
            right: 0,
            height: HEADER_HEIGHT,
            zIndex: 3,
            pointerEvents: 'none',
          }}
        >
          <TierRankPill
            rank={activeHeader.rank}
            points={activeHeader.points}
            hasScoringStarted={hasScoringStarted}
            tierSize={activeHeader.tierSize}
            incompleteTier={activeHeader.incompleteTier}
          />
        </div>
      )}

      <div ref={setScrollContainer} className="h-full w-full overflow-auto">
        <div
          style={{
            height: virtualizer.getTotalSize(),
            position: 'relative',
            width: '100%',
          }}
        >
          {virtualItems.map((virtualRow) => {
            let item = flatList[virtualRow.index];
            let isHeader = stickySet.has(virtualRow.index);
            let isCluster = item.type === FlatItemType.incompleteCluster;
            let isTitanic = item.type === FlatItemType.titanic;

            let inlineHeaderHidden =
              isHeader &&
              virtualRow.start - Math.max(0, virtualizer.scrollOffset ?? 0) <=
                SPACER_HEIGHT + SWAP_ANTI_JITTER_PX;

            return (
              <div
                key={String(virtualRow.key)}
                style={{
                  position: 'absolute',
                  transform: `translateY(${
                    virtualRow.start - (isTitanic ? SCENE_OVERLAP : 0)
                  }px)`,
                  top: 0,
                  left: 0,
                  width: '100%',
                  ...(!(isCluster || isTitanic) && {
                    height: isHeader
                      ? HEADER_HEIGHT
                      : lastBeforeHeaderSet.has(virtualRow.index)
                      ? LAST_ITEM_HEIGHT
                      : ITEM_HEIGHT,
                  }),
                  zIndex: isTitanic ? -1 : undefined,
                  ...(inlineHeaderHidden && {
                    visibility: 'hidden' as const,
                  }),
                }}
              >
                {item.type === FlatItemType.header ? (
                  <TierRankPill
                    rank={item.rank}
                    points={item.points}
                    hasScoringStarted={hasScoringStarted}
                    tierSize={item.tierSize}
                    incompleteTier={item.incompleteTier}
                  />
                ) : item.type === FlatItemType.incompleteCluster ? (
                  <IncompleteAvatarGrid users={item.users} />
                ) : item.type === FlatItemType.titanic ? (
                  <TitanicScene
                    className={makeClassName(
                      isFinalApproach ? 'opacity-100' : 'opacity-0',
                    )}
                    flipped={prefersLeftHandMode}
                    scrollContainerRef={scrollContainerSyntheticRef}
                    itemStart={virtualRow.start}
                    containerHeight={containerHeightPx}
                  />
                ) : (
                  <MobileBallotItem
                    item={item.user}
                    itemIndex={item.withinGroupIndex}
                    points={item.points}
                    useStrictScore={useStrictScore}
                    isCurrentUser={item.user.key === currentUserId}
                    isOwner={ownerUserId === item.user.key}
                    onSelectBallot={onSelectBallot}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}));

export default MobileScoredList;
